import dateFormat from "dateformat";

/**
 * cr_event_log stores the Sibro action as SIBRO_<ACTION>. The prefix is noise once the row already
 * says its source, so it is stripped here rather than in the API - the raw event_type is what the
 * backend logs and what anyone querying the table directly will search for. CKYC event types
 * (CKYC_VERIFY, CKYC_STATUS...) keep their prefix, since that is the whole name of the call.
 */
export const eventLabel = (eventType) =>
  eventType ? eventType.replace(/^SIBRO_/, "").replace(/_/g, " ") : "-";

/**
 * The source column as the tab shows it. CKYC rows are written under INSURER, which is the writer's
 * word for the system called, not the word anyone reading this tab is looking for.
 */
export const eventSourceLabel = (source) => {
  switch (source) {
    case "SIBRO":
      return "Sibro";
    case "INSURER":
      return "CKYC";
    default:
      return source || "-";
  }
};

/**
 * An order's trail is not filed entirely under the order: everything the policy triggers once it
 * exists is logged against the policy. Not a column, but the detail modal still says which record
 * a call was about.
 */
export const eventScopeLabel = (entityType) => {
  switch (entityType) {
    case "ODR":
      return "Order";
    case "PCY":
      return "Policy";
    default:
      return entityType || "-";
  }
};

/**
 * Not renderStatus from CommonUtils - that maps payment and order statuses, and EventStatus is a
 * different vocabulary (SUCCESS / FAILED / PARTIAL) that would fall through to its default branch.
 */
export const renderEventStatus = (status) => {
  switch (status) {
    case "SUCCESS":
      return (
        <span className="badge bg-success-subtle text-success text-uppercase fs-12">
          Success
        </span>
      );
    case "FAILED":
      // danger, not error: bg-error-subtle/text-error are not Bootstrap classes and are defined
      // nowhere in this app, so the badge fell back to .badge's own white text on no background -
      // which rendered as an empty cell. The text-* utilities carry !important, so they also beat
      // .badge's colour without depending on stylesheet order.
      return (
        <span className="badge bg-danger-subtle text-danger text-uppercase fs-12">
          Failed
        </span>
      );
    case "PARTIAL":
      return (
        <span className="badge bg-warning-subtle text-warning text-uppercase fs-12">
          Partial
        </span>
      );
    default:
      return <div>{status || "-"}</div>;
  }
};


function useEventLogColumns(handleViewPayload) {
  const payloadLink = (row, mode, payload) =>
    payload ? (
      <span
        className="text-primary"
        style={{ cursor: "pointer" }}
        onClick={() => handleViewPayload(row, mode)}
      >
        View
      </span>
    ) : (
      "-"
    );

  const eventColumns = [
    {
      header: "Date & Time",
      accessorKey: "createdAt",
      cell: (props) =>
        props?.getValue()
          ? dateFormat(new Date(props.getValue()), "dd/mm/yyyy hh:MM TT")
          : "-",
      size: 170,
    },
    {
      header: "Source",
      accessorKey: "source",
      cell: (props) => eventSourceLabel(props?.getValue()),
      size: 110,
    },
    {
      header: "Event",
      accessorKey: "eventType",
      cell: (props) => eventLabel(props?.getValue()),
      size: 220,
    },
    {
      header: "Request",
      accessorKey: "requestData",
      cell: (props) =>
        payloadLink(props.row.original, "request", props?.getValue()),
      size: 120,
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: (props) => renderEventStatus(props?.getValue()),
      size: 120,
    },
    {
      header: "Response",
      accessorKey: "apiResponse",
      cell: (props) =>
        payloadLink(props.row.original, "response", props?.getValue()),
      size: 120,
    },
  ];

  return { eventColumns };
}

export default useEventLogColumns;
